'use client';

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  type CSSProperties,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { Plus, Trash, Info, Function, Hash } from '@phosphor-icons/react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Expression {
  column: string;
  formula: string;
}

interface ComputeConfigProps {
  nodeId: string;
  config: { expressions?: Expression[] };
}

// ── Column hint ───────────────────────────────────────────────────────────────

/** Highlights $colName tokens in a formula string for the preview line */
function FormulaPreview({ formula }: { formula: string }) {
  if (!formula) return null;
  const parts = formula.split(/(\$[\w\u4e00-\u9fa5]+)/g);
  return (
    <div className="mt-1 font-mono text-[11px] text-zinc-400 leading-relaxed break-all">
      {parts.map((part, i) =>
        part.startsWith('$') ? (
          <span key={i} className="text-emerald-600 font-semibold">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </div>
  );
}

// ── FormulaInput ──────────────────────────────────────────────────────────────

interface FormulaInputProps {
  value: string;
  onChange: (val: string) => void;
  availableColumns: string[];
  placeholder?: string;
}

interface MenuState {
  /** Index in the formula string where '$' was typed */
  dollarIdx: number;
  /** Text typed after '$' so far (used as filter) */
  filter: string;
  style: CSSProperties;
}

function FormulaInput({ value, onChange, availableColumns, placeholder }: FormulaInputProps) {
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Columns matching current filter
  const filtered = useMemo(() => {
    if (!menu) return [];
    const q = menu.filter.toLowerCase();
    return q
      ? availableColumns.filter((c) => c.toLowerCase().includes(q))
      : availableColumns;
  }, [menu, availableColumns]);

  // Reset keyboard selection whenever the filter changes
  useEffect(() => setSelectedIdx(0), [menu?.filter]);

  // ── Menu position ──────────────────────────────────────────────────────────

  const computeMenuStyle = useCallback((): CSSProperties => {
    const ta = textareaRef.current;
    if (!ta) return {};
    const rect = ta.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    return {
      position: 'fixed',
      left: rect.left,
      width: Math.min(rect.width, 300),
      ...(spaceBelow > 220
        ? { top: rect.bottom + 3 }
        : { bottom: window.innerHeight - rect.top + 3 }),
      zIndex: 9999,
    };
  }, []);

  // ── Autocomplete state update ──────────────────────────────────────────────

  const updateMenu = useCallback(
    (val: string, cursor: number) => {
      const before = val.slice(0, cursor);
      const lastDollar = before.lastIndexOf('$');
      if (lastDollar === -1) { setMenu(null); return; }

      const afterDollar = before.slice(lastDollar + 1);
      // Only show menu when there are no whitespace / operator chars after $
      if (/[\s+\-*/()=<>!&|,]/.test(afterDollar)) { setMenu(null); return; }

      setMenu({ dollarIdx: lastDollar, filter: afterDollar, style: computeMenuStyle() });
    },
    [computeMenuStyle]
  );

  // ── Insert selected column ─────────────────────────────────────────────────

  const insertColumn = useCallback(
    (colName: string) => {
      if (!menu) return;
      const cursor = textareaRef.current?.selectionStart ?? value.length;
      const newVal =
        value.slice(0, menu.dollarIdx) +
        '$' + colName +
        value.slice(cursor);
      onChange(newVal);
      setMenu(null);
      // Restore focus and move cursor to just after the inserted token
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.focus();
        const pos = menu.dollarIdx + 1 + colName.length;
        ta.setSelectionRange(pos, pos);
      });
    },
    [menu, onChange, value]
  );

  // ── Event handlers ─────────────────────────────────────────────────────────

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    updateMenu(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!menu || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (filtered[selectedIdx]) insertColumn(filtered[selectedIdx]);
    } else if (e.key === 'Escape') {
      setMenu(null);
    }
  };

  const handleCursorMove = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    updateMenu(ta.value, ta.selectionStart ?? 0);
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        rows={2}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleCursorMove}
        onSelect={handleCursorMove}
        placeholder={placeholder}
        spellCheck={false}
        className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none font-mono placeholder:text-zinc-400 transition-colors"
      />

      {/* Autocomplete dropdown */}
      {menu && createPortal(
        <div
          style={menu.style}
          className="bg-white rounded-xl border border-zinc-200 shadow-2xl overflow-hidden"
          // mouseDown before blur so click is registered
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Header */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-50 border-b border-zinc-100">
            <Hash className="w-3 h-3 text-emerald-500" weight="bold" />
            <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
              引用列名
            </span>
            {menu.filter && (
              <span className="ml-auto text-[10px] text-zinc-400 font-mono">
                ${menu.filter}…
              </span>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-zinc-400 text-center">
              {availableColumns.length === 0
                ? '暂无可用列（请先上传数据文件）'
                : '未找到匹配列'}
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto">
              {filtered.map((col, i) => (
                <button
                  key={col}
                  onClick={() => insertColumn(col)}
                  className={[
                    'w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors',
                    i === selectedIdx
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'hover:bg-zinc-50 text-zinc-700',
                  ].join(' ')}
                >
                  <span className="font-mono font-semibold text-emerald-500 flex-shrink-0">$</span>
                  <span className="font-medium truncate">{col}</span>
                </button>
              ))}
            </div>
          )}

          {/* Footer hint */}
          <div className="px-3 py-1.5 bg-zinc-50 border-t border-zinc-100 text-[10px] text-zinc-400 flex items-center gap-2">
            <kbd className="px-1 py-0.5 bg-zinc-100 rounded text-[9px] font-mono">↑↓</kbd> 导航
            <kbd className="px-1 py-0.5 bg-zinc-100 rounded text-[9px] font-mono">Enter</kbd> 选中
            <kbd className="px-1 py-0.5 bg-zinc-100 rounded text-[9px] font-mono">Esc</kbd> 关闭
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ComputeConfig({ nodeId, config }: ComputeConfigProps) {
  const { updateNodeConfig } = useWorkflowStore();
  const inputColumns = useNodeInputColumns(nodeId);

  const [expressions, setExpressions] = useState<Expression[]>(
    config.expressions?.length ? config.expressions : [{ column: '', formula: '' }]
  );

  // Column names from the upstream node for formula autocomplete
  const availableColumns = useMemo(() => {
    return inputColumns?.map(({ column }) => column.name) ?? [];
  }, [inputColumns]);

  const save = (next: Expression[]) => {
    updateNodeConfig(nodeId, { type: 'compute', params: { expressions: next } });
  };

  const update = (index: number, patch: Partial<Expression>) => {
    const next = expressions.map((e, i) => (i === index ? { ...e, ...patch } : e));
    setExpressions(next);
    save(next);
  };

  const add = () => {
    const next = [...expressions, { column: '', formula: '' }];
    setExpressions(next);
    save(next);
  };

  const remove = (index: number) => {
    const next = expressions.filter((_, i) => i !== index);
    setExpressions(next);
    save(next);
  };

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {expressions.map((expr, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="p-3 bg-zinc-50 rounded-xl border border-zinc-100 space-y-2"
          >
            {/* Column name + delete button */}
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="新列名（如：税后金额）"
                  value={expr.column}
                  onChange={(e) => update(index, { column: e.target.value })}
                  className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                />
              </div>
              {expressions.length > 1 && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => remove(index)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                >
                  <Trash className="w-4 h-4" />
                </motion.button>
              )}
            </div>

            {/* Formula label */}
            <div className="flex items-center gap-1.5">
              <Function className="w-3 h-3 text-emerald-500" weight="bold" />
              <span className="text-[11px] font-medium text-zinc-500">公式</span>
              {availableColumns.length > 0 && (
                <span className="text-[10px] text-zinc-400 ml-auto">
                  输入 <code className="bg-zinc-100 px-1 rounded font-mono">$</code> 引用列名
                </span>
              )}
            </div>

            {/* Formula input with $ autocomplete */}
            <FormulaInput
              value={expr.formula}
              onChange={(val) => update(index, { formula: val })}
              availableColumns={availableColumns}
              placeholder="例如：$price * $qty * (1 - $discount)"
            />

            {/* Inline preview: highlight $colName tokens */}
            {expr.formula && <FormulaPreview formula={expr.formula} />}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add button */}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={add}
        className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
      >
        <Plus className="w-4 h-4" />
        添加计算字段
      </motion.button>

      {/* Help panel */}
      <div className="space-y-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
        <div className="flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
          <span className="text-xs font-medium text-blue-700">使用说明</span>
        </div>
        <ul className="text-xs text-blue-700 space-y-1 ml-5 list-disc">
          <li>
            用 <code className="bg-blue-100 px-1 rounded font-mono">$列名</code> 引用当前数据中的列，输入
            <code className="bg-blue-100 px-1 rounded font-mono ml-1">$</code> 自动弹出列选择
          </li>
          <li>支持四则运算：<code className="bg-blue-100 px-1 rounded font-mono">+ - * /</code> 和括号</li>
          <li>
            示例：<code className="bg-blue-100 px-1 rounded font-mono">$销售金额（元）* 0.9</code>
          </li>
        </ul>
      </div>
    </div>
  );
}
